import { useEffect } from 'react'
import { useRecoilState } from 'recoil'
import { getEditorPageContent } from '@/api/editorPageContent'
import { editorPageContentAtomFamily } from '@/atoms/editorTextBoxAtoms'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'

const contentRequestsInFlight = new Set<string>()

type UseEditorPageContentOptions = {
  fetch?: boolean
}

export const useEditorPageContent = (
  pageId: string | null | undefined,
  options: UseEditorPageContentOptions = {},
) => {
  const shouldFetch = options.fetch ?? true
  const [bundle, setBundle] = useRecoilState(
    editorPageContentAtomFamily(pageId ?? ''),
  )
  const handleError = useErrorHandler('useEditorPageContent')

  useEffect(() => {
    if (
      !pageId ||
      !shouldFetch ||
      bundle !== null ||
      contentRequestsInFlight.has(pageId)
    )
      return
    contentRequestsInFlight.add(pageId)
    getEditorPageContent(pageId)
      .then(({ data }) => setBundle(data))
      .catch((error: unknown) =>
        handleError(
          'The text boxes for this page could not be loaded',
          error instanceof Error ? error : new Error(String(error)),
        ),
      )
      .finally(() => contentRequestsInFlight.delete(pageId))
  }, [pageId, shouldFetch, bundle, setBundle, handleError])

  return pageId ? bundle : null
}

export default useEditorPageContent
